import { ZodType } from 'zod';
import { Profile } from './profile.js';
import { AnswerSession } from './answer-session.js';
import { OramaInterface } from './common.js';
import { flattenZodSchema, formatDuration } from './lib/utils.js';
export class CollectionManager {
    constructor(config) {
        Object.defineProperty(this, "url", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "collectionID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "writeAPIKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "readAPIKey", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "oramaInterface", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "profile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.url = config.url;
        this.writeAPIKey = config.writeAPIKey;
        this.readAPIKey = config.readAPIKey;
        this.collectionID = config.collectionID;
        this.oramaInterface = new OramaInterface({
            baseURL: this.url,
            writeAPIKey: this.writeAPIKey,
            readAPIKey: this.readAPIKey,
        });
        this.profile = new Profile({
            endpoint: this.url,
            apiKey: this.readAPIKey,
        });
    }
    async search(query) {
        const start = +new Date();
        const { datasourceIDs, indexes, ...restQuery } = query;
        const result = await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/search`,
            body: {
                ...restQuery,
                indexes: datasourceIDs || indexes,
            },
            method: 'POST',
            securityLevel: 'read-query',
        });
        const elapsed = +new Date() - start;
        return {
            ...result,
            elapsed: {
                raw: elapsed,
                formatted: formatDuration(elapsed),
            },
        };
    }
    getStats(collectionID) {
        return this.oramaInterface.request({
            url: `/v1/collections/${collectionID}/stats`,
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    async createIndex(config) {
        const body = {
            id: config.id,
            embedding: config.embeddings,
        };
        await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/indexes/create`,
            body,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    async deleteIndex(indexID) {
        await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/indexes/delete`,
            body: { index_id_to_delete: indexID },
            method: 'POST',
            securityLevel: 'write',
        });
    }
    setIndex(id) {
        return new Index(this.collectionID, id, this.url, this.writeAPIKey, this.readAPIKey);
    }
    getAllDocsInCollection(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/list`,
            method: 'POST',
            body: { id },
            securityLevel: 'write',
        });
    }
    createAnswerSession(config) {
        if (!this.readAPIKey) {
            throw new Error('Read API key is required to create an answer session');
        }
        return new AnswerSession({
            url: this.url,
            readAPIKey: this.readAPIKey || '',
            collectionID: this.collectionID,
            ...config,
        });
    }
    async insertHook(config) {
        const body = {
            name: config.name,
            code: config.code,
        };
        await this.oramaInterface.request({
            url: `/v1/collections/${config.collectionID}/hooks/create`,
            body,
            method: 'POST',
            securityLevel: 'write',
        });
        return {
            hookID: body.name,
            collectionID: config.collectionID,
            code: body.code,
        };
    }
    insertSegment(segment) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/segments/insert`,
            body: segment,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    getSegment(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/segments/get`,
            body: { segment_id: id },
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    getAllSegments() {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/segments/all`,
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    deleteSegment(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/segments/delete`,
            body: { id },
            method: 'POST',
            securityLevel: 'write',
        });
    }
    updateSegment(segment) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/segments/update`,
            body: segment,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    insertTrigger(trigger) {
        if (!trigger.segment_id) {
            throw new Error('You cannot insert a trigger without a segment_id');
        }
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/triggers/insert`,
            body: trigger,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    getTrigger(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/triggers/get`,
            body: { trigger_id: id },
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    getAllTriggers() {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/triggers/all`,
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    deleteTrigger(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/triggers/delete`,
            body: { id },
            method: 'POST',
            securityLevel: 'write',
        });
    }
    updateTrigger(trigger) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/triggers/update`,
            body: trigger,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    insertSystemPrompt(systemPrompt) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/system_prompts/insert`,
            body: systemPrompt,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    getSystemPrompt(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/system_prompts/get`,
            body: { system_prompt_id: id },
            method: 'GET',
            securityLevel: 'read',
        });
    }
    getAllSystemPrompts() {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/system_prompts/all`,
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    deleteSystemPrompt(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/system_prompts/delete`,
            body: { id },
            method: 'POST',
            securityLevel: 'write',
        });
    }
    updateSystemPrompt(systemPrompt) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/system_prompts/update`,
            body: systemPrompt,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    validateSystemPrompt(systemPrompt) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/system_prompts/validate`,
            body: systemPrompt,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    insertTool(tool) {
        let parameters;
        switch (true) {
            case typeof tool.parameters === 'string': {
                parameters = tool.parameters;
                break;
            }
            case tool.parameters instanceof ZodType: {
                const flattenedSchema = flattenZodSchema(tool.parameters);
                parameters = JSON.stringify(flattenedSchema);
                break;
            }
            case typeof tool.parameters === 'object': {
                parameters = JSON.stringify(tool.parameters);
                break;
            }
            default:
                throw new Error('Invalid parameters type. Must be string, object or ZodType');
        }
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/tools/insert`,
            body: {
                ...tool,
                parameters,
            },
            method: 'POST',
            securityLevel: 'write',
        });
    }
    getTool(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/tools/get`,
            body: { tool_id: id },
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    getAllTools() {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/tools/all`,
            method: 'GET',
            securityLevel: 'read-query',
        });
    }
    deleteTool(id) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/tools/delete`,
            body: { id },
            method: 'POST',
            securityLevel: 'write',
        });
    }
    updateTool(tool) {
        return this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/tools/update`,
            body: tool,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    async executeTools(tools) {
        const response = await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/tools/run`,
            body: tools,
            method: 'POST',
            securityLevel: 'read',
        });
        if (response.results) {
            return {
                results: response.results.map((result) => {
                    if ('functionResult' in result) {
                        return {
                            functionResult: {
                                tool_id: result.functionResult.tool_id,
                                result: JSON.parse(result.functionResult.result),
                            },
                        };
                    }
                    if ('functionParameters' in result) {
                        return {
                            functionParameters: {
                                tool_id: result.functionParameters.tool_id,
                                result: JSON.parse(result.functionParameters.result),
                            },
                        };
                    }
                    return result;
                }),
            };
        }
        return {
            results: null,
        };
    }
    getIdentity() {
        return this.profile.getIdentity();
    }
    getUserId() {
        return this.profile.getUserId();
    }
    getAlias() {
        return this.profile.getAlias();
    }
    async identify(identity) {
        await this.profile.identify(identity);
    }
    async alias(alias) {
        await this.profile.alias(alias);
    }
    reset() {
        this.profile.reset();
    }
}
export class Index {
    constructor(collectionID, indexID, url, writeAPIKey, readAPIKey) {
        Object.defineProperty(this, "indexID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "collectionID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "oramaInterface", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.indexID = indexID;
        this.collectionID = collectionID;
        this.oramaInterface = new OramaInterface({
            baseURL: url,
            writeAPIKey: writeAPIKey,
            readAPIKey: readAPIKey,
        });
    }
    async reindex() {
        await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/indexes/${this.indexID}/reindex`,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    async insertDocuments(documents) {
        await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/indexes/${this.indexID}/insert`,
            body: documents,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    async deleteDocuments(documentIDs) {
        await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/indexes/${this.indexID}/delete`,
            body: documentIDs,
            method: 'POST',
            securityLevel: 'write',
        });
    }
    async upsertDocuments(documents) {
        await this.oramaInterface.request({
            url: `/v1/collections/${this.collectionID}/indexes/${this.indexID}/insert`,
            body: documents,
            method: 'POST',
            securityLevel: 'write',
        });
    }
}
